import assign from 'lodash/assign';
import pick from 'lodash/pick';
import toPlainObject from 'lodash/toPlainObject';

// Actions
import { getComparables } from './AddressActions';

// Models
import Filter from '../../models/Filter';

// Misc / Utils
import filterUtils from '../../utils/filterUtils';
import { encodeFullAddressForUrl, queryObjectToQueryString } from '../../utils/urlUtils';
import routingUtils from '../../utils/routingUtils';
// @ts-ignore
import { augmentError } from '../../utils/errors';
// @ts-ignore

// Types
import type { QueryDataObjectType } from '../../types/QueryDataObjectType';
import type { FilterApiType } from '../../types/ApiResponseTypes';
import type { PartialFilterType } from '../../types/Filters';
import type { UserProps } from '../../models/User';
import type Address from '../../models/Address';
import type AutocompleteAddress from '../../models/AutocompleteAddress';

const _getPartialFilterForUrl = (currentFilter: FilterApiType, partialFilter?: PartialFilterType) => {
  const newFilter = toPlainObject(new Filter(assign({}, currentFilter, partialFilter)));

  // get relevant filters, ie: non-default filters
  const filterDiff = filterUtils.getFilterDiff({
    baseFilter: toPlainObject(new Filter()),
    editedFilter: newFilter,
  });

  /**
   * When default values are truthy (ex: propertyTypes) filterDiff will only include
   * falsey values, ex: { propertyType : { house: false, townhouse: false } }. Since
   * we actually want url params to contain truthy propertyTypes
   * (propertyType=apartments-condos), we need to use the entire filter.
   *
   * Exception: to support activatedDays & deactivatedDays filters, we need to pass
   * both default (truthy) values for activeTypes
   */
  const partialFilterForUrl = pick(newFilter, Object.keys({ ...filterDiff, ...{ activeTypes: null } }));
  return partialFilterForUrl;
};

const _updateFilter = (partialFilter: PartialFilterType, updateFilter: (arg: Filter) => void) => {
  // @ts-ignore
  return () => {
    try {
      return updateFilter(new Filter(assign(toPlainObject(new Filter()), partialFilter)));
    } catch (error) {
      augmentError(error, {
        action: 'Error in FilterActions#_updateFilter',
        partialFilter: partialFilter,
      });

      throw error;
    }
  };
};

export const updateFilterAndNavigate = (
  path: string,
  partialFilter: PartialFilterType,
  user: UserProps,
  address: Address,
  updateFilter: (arg: Filter) => void,
  currentFilter: Filter,
  validAddressItem?: AutocompleteAddress,
) => {
  return async () => {
    const partialFilterForUrl = _getPartialFilterForUrl(currentFilter as unknown as FilterApiType, partialFilter);
    const { filterForGraphQL, filterForUrl } = filterUtils.partialFilterToFilterParams(partialFilterForUrl);

    // update the filter
    _updateFilter(partialFilter, updateFilter);

    const addressDetailsParams = {
      street: address.street || (validAddressItem?.street ?? null),
      city: address.city || (validAddressItem?.city ?? null),
      state: address.state || (validAddressItem?.state ?? null),
      zip: address.zip || (validAddressItem?.zip ?? null),
      unit: address.unit || (validAddressItem?.unit ?? null),
      // the address price will be overwritten by the price filter (if one is set)
      price: address.rentZestimate || undefined,
    };

    // get address without unit for url path
    const encodedAddress = encodeFullAddressForUrl({
      address: `${addressDetailsParams.street}, ${addressDetailsParams.city}, ${addressDetailsParams.state} ${addressDetailsParams.zip}`,
      unit: '',
    });

    const params: QueryDataObjectType = assign({}, addressDetailsParams, filterForGraphQL);

    // get comparables
    await getComparables(params, user, address);

    // get full query
    const filterParams = toPlainObject(filterForUrl);
    const queryParams = { ...filterParams };
    if (address.unit) {
      queryParams.unit = address.unit;
    }

    const newUrl = path + `/${encodedAddress}` + queryObjectToQueryString(toPlainObject(queryParams));

    return routingUtils.push(newUrl);
  };
};
